import * as z from "zod";
import { api } from "@/lib/http";

export type Company = {
  id: string;
  company_name: string;
  description: string;
  company_logo: string;
  created_at: string;
  updated_at: string;
};

export type TemplatePolicies = Record<
  string,
  Array<{
    entity: string;
    priority_index: number;
  }>
>;

export type Seats = {
  max_seats: number;
  used_seats: number;
  available_seats: number | null;
  is_unlimited: boolean;
};

// Zod schemas for validation
export const createCompanySchema = z.object({
  company_name: z.string().min(1, "Company name is required"),
  description: z.string().min(1, "Description is required"),
  company_logo: z.file().optional(),
  users_file: z.file().optional(),
  policies: z.array(z.string()).optional(),
});

export const updateCompanySchema = z.object({
  company_name: z.string().min(1, "Company name is required"),
  description: z.string().min(1, "Description is required"),
  company_logo: z.file().optional(),
});

// Inferred types from schemas
export type CreateCompanyPayload = z.infer<typeof createCompanySchema>;
export type UpdateCompanyPayload = z.infer<typeof updateCompanySchema>;

async function getCompany() {
  return api.raw.get("api/v1/company/").json<Company>();
}

async function getTemplatePolicies() {
  return api.raw
    .get("api/v1/policies/template-policies")
    .json<TemplatePolicies>();
}

async function getSeats() {
  return api.raw.get("api/v1/company/seats").json<Seats>();
}

async function createCompany(payload: CreateCompanyPayload) {
  const formData = new FormData();
  formData.append("company_name", payload.company_name);
  formData.append("description", payload.description);

  if (payload.company_logo) {
    formData.append("company_logo", payload.company_logo);
  }

  if (payload.users_file) {
    formData.append("users_file", payload.users_file);
  }

  if (payload.policies) {
    payload.policies.forEach((policy) => {
      formData.append("policies", policy);
    });
  }

  return api.raw.post("api/v1/company/", { body: formData }).json<Company>();
}

async function updateCompany(payload: UpdateCompanyPayload) {
  return api.raw
    .patch("api/v1/company/", { json: payload })
    .json<Company>();
}

async function updateCompanyLogo(file: File) {
  const formData = new FormData();
  formData.append("company_logo", file);
  return api.raw.put("api/v1/company/logo", { body: formData }).json<Company>();
}

async function deleteCompanyLogo() {
  return api.raw.delete("api/v1/company/logo").json();
}

const company = {
  getCompany,
  getTemplatePolicies,
  getSeats,
  createCompany,
  updateCompany,
  updateCompanyLogo,
  deleteCompanyLogo,
};

export default company;
